/**
 * TaxonomyTreeEditor (S1.1) — net-new shared component (holistic plan list).
 *
 * Left-pane editor for the 5-capable category tree: expand/collapse,
 * type-to-filter (ancestors kept so matches stay in context), drag
 * reorder/reparent with a client-side depth-cap toast (mirrors the S0.4
 * handler guards — the server re-checks), inline add-child, inactive gray.
 *
 * Selection is CONTROLLED by the consumer (route-derived on the admin page).
 * Mutations are NOT performed here — drops surface as a `TaxonomyMoveIntent`
 * so the consumer can collect the mandatory change comment first.
 */

import { useEffect, useMemo, useState } from 'react';
import { App, Button, Popover, Tag, Tooltip, Tree, Typography, theme } from 'antd';
import type { TreeDataNode, TreeProps } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { subtreeHeight, subtreeIds } from '@/hooks/useTaxonomy';
import { MAX_CATEGORY_DEPTH } from '@/types/api/productCategory';
import type { CategoryLevelConfig, ProductCategory } from '@/types/api/productCategory';
import TaxonomyQuickCreateRow from '@/components/taxonomy/TaxonomyQuickCreateRow';
import { useElevation } from '@/styles';

const { Text } = Typography;

export interface TaxonomyMoveIntent {
  nodeId: string;
  newParentId: string | null;
  /** Set only for a same-parent reorder drop (gap drop between siblings). */
  newSortOrder?: number;
  kind: 'reparent' | 'reorder';
}

export interface TaxonomyTreeEditorProps {
  nodes: ProductCategory[];
  levels: CategoryLevelConfig[];
  /** Controlled selection (route-derived on the admin page). */
  selectedId?: string | null;
  onSelect: (id: string) => void;
  /** Type-to-filter term (matched against code / nameEn / nameZh). */
  search?: string;
  /** When false (default) deactivated nodes are hidden. */
  includeInactive?: boolean;
  /** Inline add-child affordance — parent node id. Opens the quick-create popover. */
  onAddChild: (parentId: string) => void;
  /**
   * Parent id whose quick-create popover is open (route/state-derived on the host).
   * Controlled so only one popover is ever open and clicking another `+` switches
   * cleanly (no onOpenChange race).
   */
  activeQuickCreateParentId?: string | null;
  /**
   * A node was filed from the quick-create popover. `keepOpen` = true for
   * "Save & add another" (host refreshes but keeps the popover open); false for
   * "Save" (host selects the new node and closes).
   */
  onQuickCreated?: (newNodeId: string, keepOpen: boolean) => void;
  /** Dismiss the quick-create popover without filing (Cancel / Esc). */
  onQuickCreateCancel?: () => void;
  /** Drag drop landed and passed the client guards — collect comment + mutate. */
  onMove: (intent: TaxonomyMoveIntent) => void;
}

const TaxonomyTreeEditor = ({
  nodes,
  levels,
  selectedId,
  onSelect,
  search = '',
  includeInactive = false,
  onAddChild,
  activeQuickCreateParentId = null,
  onQuickCreated,
  onQuickCreateCancel,
  onMove,
}: TaxonomyTreeEditorProps) => {
  const { t, i18n } = useTranslation(['taxonomy', 'common']);
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const elevation = useElevation();
  const isZh = i18n.language.startsWith('zh');

  // Wave-2 elevation applied here (first consumer): the quick-create popover
  // container carries the layered de-border shadow + the WCAG-1.4.11 ring. Memoized
  // on the resolved primitives (stable per theme) so the `treeData` memo below is
  // not busted every render by the fresh object `useElevation()` returns.
  const {
    border: popoverBorder,
    boxShadow: popoverShadow,
    borderRadius: popoverRadius,
  } = elevation.popover;
  const popoverBodyStyle = useMemo(
    () => ({ padding: 0, border: popoverBorder, boxShadow: popoverShadow, borderRadius: popoverRadius }),
    [popoverBorder, popoverShadow, popoverRadius]
  );

  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [autoExpandSeeded, setAutoExpandSeeded] = useState(false);

  const byId = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes]);
  const enabledLevels = useMemo(
    () => new Set<number>(levels.filter((l) => l.enabled).map((l) => l.level)),
    [levels]
  );

  // Seed initial expansion: open all roots once data arrives
  useEffect(() => {
    if (!autoExpandSeeded && nodes.length > 0) {
      setExpandedKeys(nodes.filter((n) => n.depth === 1).map((n) => n.id));
      setAutoExpandSeeded(true);
    }
  }, [autoExpandSeeded, nodes]);

  // Type-to-filter: matches + their ancestors (context) + their descendants
  const { visibleIds, searchExpandedKeys } = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) {
      return {
        visibleIds: null as Set<string> | null,
        searchExpandedKeys: null as React.Key[] | null,
      };
    }
    const matched = nodes.filter(
      (n) =>
        n.code.toLowerCase().includes(term) ||
        n.nameEn.toLowerCase().includes(term) ||
        n.nameZh.toLowerCase().includes(term)
    );
    const visible = new Set<string>();
    const ancestors = new Set<string>();
    for (const node of matched) {
      for (const id of node.path.split('/')) {
        visible.add(id);
        if (id !== node.id) ancestors.add(id);
      }
      for (const id of subtreeIds(nodes, node.id)) visible.add(id);
    }
    return { visibleIds: visible, searchExpandedKeys: [...ancestors] };
  }, [nodes, search]);

  const treeData = useMemo(() => {
    const renderTitle = (node: ProductCategory): React.ReactNode => {
      const primaryName = isZh ? node.nameZh : node.nameEn;
      const secondaryName = isZh ? node.nameEn : node.nameZh;
      const levelHidden = !enabledLevels.has(node.depth);
      const childLevel = node.depth + 1;
      const canAddChild =
        node.isActive && childLevel <= MAX_CATEGORY_DEPTH && enabledLevels.has(childLevel);

      return (
        <span
          className="tw-inline-flex tw-items-center tw-gap-2"
          style={{ color: node.isActive ? token.colorText : token.colorTextQuaternary }}
        >
          <span style={{ fontWeight: node.depth === 1 ? token.fontWeightStrong : undefined }}>
            {primaryName}
          </span>
          <Text
            type="secondary"
            style={{
              fontSize: token.fontSizeSM,
              color: node.isActive ? undefined : token.colorTextQuaternary,
            }}
          >
            {secondaryName} · {node.code}
          </Text>
          {!node.isActive && <Tag bordered={false}>{t('common:status.inactive')}</Tag>}
          {node.isActive && levelHidden && (
            <Tooltip title={t('taxonomy:tree.hiddenLevelTooltip')}>
              <Tag bordered={false}>{t('taxonomy:tree.hiddenLevel')}</Tag>
            </Tooltip>
          )}
          {canAddChild && (
            <Popover
              open={activeQuickCreateParentId === node.id}
              // Fully controlled (no AntD trigger) — the `+` click sets the host
              // state, Cancel/Esc/clicking another `+` clears it. This sidesteps the
              // onOpenChange race a click-trigger popover would have when switching rows.
              trigger={[]}
              arrow={false}
              placement="rightTop"
              destroyOnHidden
              styles={{ body: popoverBodyStyle }}
              content={
                activeQuickCreateParentId === node.id ? (
                  <TaxonomyQuickCreateRow
                    parent={node}
                    nodes={nodes}
                    onCreated={(newId, keepOpen) => onQuickCreated?.(newId, keepOpen)}
                    onCancel={() => onQuickCreateCancel?.()}
                  />
                ) : null
              }
            >
              <Tooltip title={t('taxonomy:tree.addChild')}>
                <Button
                  type="text"
                  size="small"
                  icon={
                    <PlusOutlined style={{ fontSize: token.sizeXS, color: token.colorPrimary }} />
                  }
                  aria-label={t('taxonomy:tree.addChildTo', { name: primaryName })}
                  onClick={(event) => {
                    event.stopPropagation();
                    onAddChild(node.id);
                  }}
                />
              </Tooltip>
            </Popover>
          )}
        </span>
      );
    };

    const build = (parentId: string | null): TreeDataNode[] =>
      nodes
        .filter((n) => n.parentId === parentId)
        .filter((n) => includeInactive || n.isActive)
        .filter((n) => !visibleIds || visibleIds.has(n.id))
        .sort((a, b) => a.sortOrder - b.sortOrder || a.nameEn.localeCompare(b.nameEn))
        .map((n) => ({
          key: n.id,
          title: renderTitle(n),
          children: build(n.id),
        }));

    return build(null);
  }, [
    activeQuickCreateParentId,
    enabledLevels,
    includeInactive,
    isZh,
    nodes,
    onAddChild,
    onQuickCreateCancel,
    onQuickCreated,
    popoverBodyStyle,
    t,
    token,
    visibleIds,
  ]);

  const handleDrop: TreeProps['onDrop'] = (info) => {
    const dragId = String(info.dragNode.key);
    const dropId = String(info.node.key);
    const dragged = byId.get(dragId);
    const dropNode = byId.get(dropId);
    if (!dragged || !dropNode) return;

    // antd relative drop position: -1 before, 0 onto, 1 after
    const posParts = info.node.pos.split('-');
    const relativePos = info.dropPosition - Number(posParts[posParts.length - 1]);

    const newParentId = !info.dropToGap ? dropNode.id : dropNode.parentId;

    // Client-side guard prediction (server re-checks): cycle + depth cap
    if (newParentId !== null && subtreeIds(nodes, dragId).has(newParentId)) {
      message.error(t('taxonomy:errors.CYCLE'));
      return;
    }
    const newParent = newParentId ? byId.get(newParentId) : undefined;
    if (newParentId !== null && !newParent) return;
    if (newParent && !newParent.isActive) {
      message.error(t('taxonomy:errors.PARENT_INACTIVE'));
      return;
    }
    const newDepth = (newParent?.depth ?? 0) + 1;
    if (newDepth + subtreeHeight(nodes, dragId) - 1 > MAX_CATEGORY_DEPTH) {
      message.error(t('taxonomy:errors.SUBTREE_DEPTH_EXCEEDED'));
      return;
    }

    if (newParentId !== (dragged.parentId ?? null)) {
      onMove({ nodeId: dragId, newParentId, kind: 'reparent' });
      return;
    }

    // Same parent: gap drop = reorder; drop onto own parent = no-op
    if (!info.dropToGap) return;

    const siblings = nodes
      .filter((n) => (n.parentId ?? null) === newParentId && n.id !== dragId)
      .sort((a, b) => a.sortOrder - b.sortOrder || a.nameEn.localeCompare(b.nameEn));
    const dropIndex = siblings.findIndex((n) => n.id === dropId);
    if (dropIndex < 0) return;
    const insertIndex = relativePos === -1 ? dropIndex : dropIndex + 1;
    const prev = siblings[insertIndex - 1];
    const next = siblings[insertIndex];
    let newSortOrder: number;
    if (prev && next) newSortOrder = (prev.sortOrder + next.sortOrder) / 2;
    else if (next) newSortOrder = next.sortOrder - 10;
    else if (prev) newSortOrder = prev.sortOrder + 10;
    else return;

    onMove({ nodeId: dragId, newParentId, newSortOrder, kind: 'reorder' });
  };

  if (treeData.length === 0) {
    return (
      <div className="tw-py-10 tw-text-center">
        <Text type="secondary">
          {search.trim() ? t('taxonomy:tree.noMatch') : t('taxonomy:tree.empty')}
        </Text>
      </div>
    );
  }

  return (
    <Tree
      blockNode
      showLine={{ showLeafIcon: false }}
      treeData={treeData}
      selectedKeys={selectedId ? [selectedId] : []}
      onSelect={(keys) => {
        if (keys.length > 0) onSelect(String(keys[0]));
      }}
      expandedKeys={
        searchExpandedKeys ? [...new Set([...expandedKeys, ...searchExpandedKeys])] : expandedKeys
      }
      onExpand={(keys) => setExpandedKeys(keys)}
      draggable={{
        icon: false,
        nodeDraggable: (node) => {
          const record = node ? byId.get(String(node.key)) : undefined;
          return Boolean(record?.isActive);
        },
      }}
      onDrop={handleDrop}
    />
  );
};

export default TaxonomyTreeEditor;
